/**
 * SVG chart primitives.
 *
 * Shared specs (see the data-viz method):
 *   bars <= 24px thick, 4px rounded data-end, square at the baseline
 *   lines 2px, round join/cap; markers r >= 4 with a 2px surface ring
 *   a 2px surface-coloured gap separates touching marks
 *   gridlines and axes are solid hairlines, one step off the surface
 *   every mark carries a hover + keyboard-focus tooltip; hit areas are >= 24px
 */

import { svg, attachTip, showTooltip, hideTooltip } from './tooltip.js';
import { niceTicks, fmtInt } from './format.js';

const SURFACE = '#ffffff';
const GAP = 2; // the surface gap, in px
const MAX_BAR = 24;

function clear(node) {
  while (node.firstChild) node.removeChild(node.firstChild);
}

function truncate(text, maxChars) {
  const s = String(text);
  return s.length <= maxChars ? s : `${s.slice(0, Math.max(1, maxChars - 1)).trimEnd()}…`;
}

/** SVG units track CSS pixels, so text never scales with the card width. */
function measure(container, fallback = 720) {
  const w = container.clientWidth || container.parentElement?.clientWidth || fallback;
  return Math.max(320, Math.round(w));
}

function mount(container, width, height) {
  clear(container);
  const root = svg('svg', {
    class: 'chart',
    viewBox: `0 0 ${width} ${height}`,
    role: 'img',
    preserveAspectRatio: 'xMidYMid meet'
  });
  container.appendChild(root);
  return root;
}

/** Rounded at the data end only; square where it meets the baseline. */
function barPath(x, y, w, h, r, direction = 'up') {
  const radius = Math.max(0, Math.min(r, w / 2, h));
  if (h <= 0.5) return '';
  if (direction === 'up') {
    return `M${x},${y + h} L${x},${y + radius} Q${x},${y} ${x + radius},${y}
            L${x + w - radius},${y} Q${x + w},${y} ${x + w},${y + radius}
            L${x + w},${y + h} Z`;
  }
  // horizontal, growing right
  return `M${x},${y} L${x + w - radius},${y} Q${x + w},${y} ${x + w},${y + radius}
          L${x + w},${y + h - radius} Q${x + w},${y + h} ${x + w - radius},${y + h}
          L${x},${y + h} Z`;
}

function gridAndAxis(root, { left, top, plotW, plotH, ticks, scaleY, formatTick }) {
  for (const t of ticks) {
    const y = top + plotH - scaleY(t);
    if (t > 0) root.appendChild(svg('line', { class: 'gridline', x1: left, x2: left + plotW, y1: y, y2: y }));
    const label = svg('text', { class: 'axis-label-tab', x: left - 8, y: y + 4, 'text-anchor': 'end' });
    label.textContent = formatTick ? formatTick(t) : fmtInt(t);
    root.appendChild(label);
  }
  root.appendChild(
    svg('line', { class: 'baseline', x1: left, x2: left + plotW, y1: top + plotH, y2: top + plotH })
  );
}

// ---------------------------------------------------------------------------
// Grouped / single column chart
// ---------------------------------------------------------------------------

/**
 * @param {HTMLElement} container
 * @param {{categories:string[], series:Array<{key:string,name:string,color:string,values:number[]}>,
 *          height?:number, formatValue?:Function, formatTick?:Function,
 *          labelLast?:boolean, subtitleFor?:Function}} opts
 */
export function columnChart(container, opts) {
  const {
    categories,
    series,
    height = 260,
    formatValue = fmtInt,
    formatTick = fmtInt,
    labelLast = true,
    subtitleFor = null
  } = opts;

  const width = measure(container);
  const left = 46;
  const right = 12;
  const top = 14;
  const axisBand = 28;
  const plotW = width - left - right;
  const plotH = height - top - axisBand;

  const root = mount(container, width, height);

  const max = Math.max(1, ...series.flatMap(s => s.values.filter(Number.isFinite)));
  const ticks = niceTicks(max, 4);
  const yMax = ticks[ticks.length - 1];
  const scaleY = v => (v / yMax) * plotH;

  gridAndAxis(root, { left, top, plotW, plotH, ticks, scaleY, formatTick });

  const band = plotW / categories.length;
  const groupW = Math.min(band * 0.62, MAX_BAR * series.length + GAP * (series.length - 1));
  const barW = Math.max(3, (groupW - GAP * (series.length - 1)) / series.length);

  const widestLabel = Math.max(...categories.map(c => c.length)) * 6.6;
  const labelEvery = Math.max(1, Math.ceil(widestLabel / Math.max(1, band)));

  categories.forEach((cat, i) => {
    const bandX = left + band * i;
    const groupX = bandX + (band - groupW) / 2;

    // One hit area per category covers the whole band, so the reader aims at a
    // quarter rather than at a 10px bar.
    const hit = svg('rect', {
      class: 'hit',
      x: bandX,
      y: top,
      width: band,
      height: plotH + axisBand,
      role: 'button',
      'aria-label': `${cat}: ${series.map(s => `${s.name} ${formatValue(s.values[i])}`).join(', ')}`
    });
    attachTip(hit, () => ({
      title: cat,
      sub: subtitleFor ? subtitleFor(i) : undefined,
      rows: series.map(s => ({
        label: s.name,
        value: formatValue(s.values[i]),
        color: s.color
      }))
    }));

    series.forEach((s, si) => {
      const v = s.values[i];
      if (!Number.isFinite(v) || v <= 0) return;
      const h = scaleY(v);
      const x = groupX + si * (barW + GAP);
      const y = top + plotH - h;
      root.appendChild(
        svg('path', { class: 'mark', d: barPath(x, y, barW, h, 4), fill: s.color })
      );

      // Direct-label only the final column, and only when the bar is tall
      // enough to carry a label without crowding its neighbour.
      if (labelLast && i === categories.length - 1 && series.length <= 2 && v >= yMax * 0.08) {
        const label = svg('text', {
          class: 'value-label',
          x: x + barW / 2,
          y: y - 5,
          'text-anchor': 'middle'
        });
        label.textContent = formatValue(v);
        root.appendChild(label);
      }
    });

    // Thin the axis labels when the band is narrower than the text, always
    // keeping the last one so the reader can anchor the series.
    const showTick = band >= cat.length * 6.6 || i % labelEvery === 0 || i === categories.length - 1;
    if (showTick) {
      const tick = svg('text', {
        class: 'axis-label',
        x: bandX + band / 2,
        y: top + plotH + 17,
        'text-anchor': 'middle'
      });
      tick.textContent = cat;
      root.appendChild(tick);
    }

    root.appendChild(hit);
  });

  return root;
}

// ---------------------------------------------------------------------------
// Line / area chart with a snapping crosshair
// ---------------------------------------------------------------------------

export function lineChart(container, opts) {
  const {
    categories,
    series,
    height = 260,
    formatValue = fmtInt,
    formatTick = fmtInt,
    area = false,
    zeroBased = true
  } = opts;

  const width = measure(container);
  const left = 46;
  const right = 46;
  const top = 14;
  const axisBand = 28;
  const plotW = width - left - right;
  const plotH = height - top - axisBand;

  const root = mount(container, width, height);

  const all = series.flatMap(s => s.values.filter(Number.isFinite));
  const max = Math.max(1, ...all);
  const min = zeroBased ? 0 : Math.min(...all);
  const ticks = niceTicks(max, 4).filter(t => t >= min);
  const yMax = ticks[ticks.length - 1] || max;
  const scaleY = v => ((v - min) / (yMax - min || 1)) * plotH;

  gridAndAxis(root, { left, top, plotW, plotH, ticks, scaleY, formatTick });

  const stepX = categories.length > 1 ? plotW / (categories.length - 1) : 0;
  const px = i => left + stepX * i;
  const py = v => top + plotH - scaleY(v);

  for (const s of series) {
    const pts = s.values
      .map((v, i) => (Number.isFinite(v) ? [px(i), py(v)] : null))
      .filter(Boolean);
    if (!pts.length) continue;

    if (area) {
      const d =
        `M${pts[0][0]},${top + plotH} ` +
        pts.map(p => `L${p[0]},${p[1]}`).join(' ') +
        ` L${pts.at(-1)[0]},${top + plotH} Z`;
      root.appendChild(svg('path', { d, fill: s.color, 'fill-opacity': 0.1 }));
    }

    root.appendChild(
      svg('path', {
        class: 'mark',
        d: `M${pts.map(p => `${p[0]},${p[1]}`).join(' L')}`,
        fill: 'none',
        stroke: s.color,
        'stroke-width': 2,
        'stroke-linejoin': 'round',
        'stroke-linecap': 'round'
      })
    );

    // End marker with its 2px surface ring, plus a direct end-label.
    const last = pts.at(-1);
    root.appendChild(svg('circle', { cx: last[0], cy: last[1], r: 4.5, fill: s.color, stroke: SURFACE, 'stroke-width': 2 }));
    if (series.length <= 3) {
      const label = svg('text', { class: 'value-label', x: last[0] + 8, y: last[1] + 4 });
      label.textContent = formatValue(s.values.filter(Number.isFinite).at(-1));
      root.appendChild(label);
    }
  }

  categories.forEach((cat, i) => {
    if (categories.length > 8 && i % 2 === 1 && i !== categories.length - 1) return;
    const tick = svg('text', {
      class: 'axis-label',
      x: px(i),
      y: top + plotH + 17,
      'text-anchor': 'middle'
    });
    tick.textContent = cat;
    root.appendChild(tick);
  });

  // --- crosshair: the reader aims at an X, never at a 2px line --------------
  const crosshair = svg('line', { class: 'crosshair', y1: top, y2: top + plotH, opacity: 0 });
  root.appendChild(crosshair);
  const dots = svg('g', { opacity: 0 });
  root.appendChild(dots);

  const overlay = svg('rect', { class: 'hit', x: left, y: top, width: plotW, height: plotH });
  root.appendChild(overlay);

  const at = idx => {
    crosshair.setAttribute('x1', px(idx));
    crosshair.setAttribute('x2', px(idx));
    crosshair.setAttribute('opacity', 1);
    clear(dots);
    dots.setAttribute('opacity', 1);
    for (const s of series) {
      const v = s.values[idx];
      if (!Number.isFinite(v)) continue;
      dots.appendChild(
        svg('circle', { cx: px(idx), cy: py(v), r: 4.5, fill: s.color, stroke: SURFACE, 'stroke-width': 2 })
      );
    }
  };

  const readout = idx => ({
    title: categories[idx],
    rows: series
      .filter(s => Number.isFinite(s.values[idx]))
      .map(s => ({ label: s.name, value: formatValue(s.values[idx]), color: s.color }))
  });

  // clientX -> viewBox units -> nearest category index.
  const indexFor = evt => {
    const box = root.getBoundingClientRect();
    const localX = ((evt.clientX - box.left) / box.width) * width;
    const idx = Math.round((localX - left) / (stepX || 1));
    return Math.max(0, Math.min(categories.length - 1, idx));
  };

  overlay.addEventListener('pointermove', evt => {
    const idx = indexFor(evt);
    at(idx);
    showTooltip(readout(idx), evt.clientX, evt.clientY);
  });
  overlay.addEventListener('pointerleave', () => {
    crosshair.setAttribute('opacity', 0);
    dots.setAttribute('opacity', 0);
    hideTooltip();
  });

  // Keyboard parity with hover.
  overlay.setAttribute('tabindex', '0');
  let kb = categories.length - 1;
  overlay.addEventListener('focus', () => {
    at(kb);
    const box = overlay.getBoundingClientRect();
    showTooltip(readout(kb), box.left + box.width * (kb / Math.max(1, categories.length - 1)), box.top + 20);
  });
  overlay.addEventListener('keydown', evt => {
    if (evt.key !== 'ArrowLeft' && evt.key !== 'ArrowRight') return;
    evt.preventDefault();
    kb = Math.max(0, Math.min(categories.length - 1, kb + (evt.key === 'ArrowRight' ? 1 : -1)));
    at(kb);
    const box = overlay.getBoundingClientRect();
    showTooltip(readout(kb), box.left + box.width * (kb / Math.max(1, categories.length - 1)), box.top + 20);
  });
  overlay.addEventListener('blur', () => {
    crosshair.setAttribute('opacity', 0);
    dots.setAttribute('opacity', 0);
    hideTooltip();
  });

  return root;
}

// ---------------------------------------------------------------------------
// Horizontal bars - magnitude, long category names, emphasis
// ---------------------------------------------------------------------------

export function hBarChart(container, opts) {
  const {
    rows, // [{label, value, color?, emphasis?, sub?}]
    formatValue = fmtInt,
    barHeight = 20,
    gap = 10,
    labelWidth = 150,
    valueWidth = 62,
    color = 'var(--series-1)',
    dimOthers = false
  } = opts;

  const width = measure(container);
  const height = rows.length * (barHeight + gap) + gap;
  const plotLeft = labelWidth + 8;
  const plotW = width - plotLeft - valueWidth - 8;

  const root = mount(container, width, height);
  const max = Math.max(1, ...rows.map(r => r.value || 0));

  rows.forEach((row, i) => {
    const y = gap + i * (barHeight + gap);
    const w = Math.max(0, (row.value / max) * plotW);
    const fill = row.color || color;
    const dim = dimOthers && !row.emphasis;

    const label = svg('text', {
      class: 'axis-label',
      x: labelWidth,
      y: y + barHeight / 2 + 4,
      'text-anchor': 'end',
      fill: row.emphasis ? 'var(--ink)' : null,
      'font-weight': row.emphasis ? 700 : null
    });
    // Truncate rather than let a long name run off the card; the full name is
    // in the tooltip and the table view.
    label.textContent = truncate(row.label, Math.floor(labelWidth / 6.3));
    root.appendChild(label);

    root.appendChild(
      svg('path', {
        class: `mark${dim ? ' mark-dim' : ''}`,
        d: barPath(plotLeft, y, w, barHeight, 4, 'right'),
        fill
      })
    );

    // Value at the tip - outside the bar, so it can never be clipped by it.
    const value = svg('text', {
      class: 'value-label',
      x: plotLeft + w + 8,
      y: y + barHeight / 2 + 4,
      fill: row.emphasis ? 'var(--ink)' : null
    });
    value.textContent = formatValue(row.value);
    root.appendChild(value);

    // Hit area spans the full row and clears the 24px minimum.
    const hit = svg('rect', {
      class: 'hit',
      x: 0,
      y: y - gap / 2,
      width,
      height: barHeight + gap,
      role: 'button',
      'aria-label': `${row.label}: ${formatValue(row.value)}`
    });
    attachTip(hit, () => ({
      title: row.label,
      sub: row.sub,
      rows: [{ label: row.valueLabel || '', value: formatValue(row.value), color: fill, shape: 'dot' }]
    }));
    root.appendChild(hit);
  });

  return root;
}

// ---------------------------------------------------------------------------
// 100% stacked bar - part-to-whole
// ---------------------------------------------------------------------------

export function stackedBar(container, opts) {
  const { segments, formatValue = fmtInt, height = 46, valueLabel = 'listings', totalLabel = 'of active listings',
    showValues = true, bracketKeys = [] } = opts;
  const width = measure(container);
  const root = mount(container, width, height + (bracketKeys.length ? 16 : 0));

  // Empty categories must not reserve a gap or a tooltip target.
  const visibleSegments = segments.filter(seg => seg.value > 0);
  if (!visibleSegments.length) return root;
  const total = visibleSegments.reduce((a, s) => a + s.value, 0);
  const usable = width - GAP * (visibleSegments.length - 1);
  const bracketed = new Set(bracketKeys);
  let bracketStart = null;
  let bracketEnd = 0;

  let x = 0;
  visibleSegments.forEach((seg, i) => {
    const w = (seg.value / total) * usable;
    if (bracketed.has(seg.key)) {
      bracketStart ??= x;
      bracketEnd = x + w;
    }
    if (w > 0.5) {
      const r = 4;
      const first = i === 0;
      const last = i === visibleSegments.length - 1;
      // Round only the outer ends of the whole bar.
      const d = roundedSegment(x, 0, w, height, r, first, last);
      root.appendChild(svg('path', { class: 'mark', d, fill: seg.color }));

      // Label inside only when it comfortably fits; otherwise the legend and
      // tooltip carry it (the value is also in the table view).
      const text = formatValue(seg.value);
      if (showValues && w > text.length * 8 + 22) {
        const label = svg('text', {
          x: x + w / 2,
          y: height / 2 + 4,
          'text-anchor': 'middle',
          fill: seg.labelColor || '#ffffff',
          'font-size': 12,
          'font-weight': 700
        });
        label.textContent = text;
        root.appendChild(label);
      }
    }

    const hit = svg('rect', {
      class: 'hit',
      x,
      y: 0,
      width: Math.max(w + GAP, 8),
      height,
      role: 'button',
      'aria-label': `${seg.label}: ${formatValue(seg.value)}`
    });
    attachTip(hit, () => ({
      title: seg.label,
      rows: [
        { label: valueLabel, value: formatValue(seg.value), color: seg.color, shape: 'dot' },
        { label: totalLabel, value: `${((seg.value / total) * 100).toFixed(1)}%` }
      ]
    }));
    root.appendChild(hit);

    x += w + GAP;
  });

  if (bracketStart != null) {
    root.appendChild(svg('path', {
      class: 'stack-bracket',
      d: `M${Math.max(0.5, bracketStart)},${height + 5} V${height + 12} H${Math.min(width - 0.5, bracketEnd)} V${height + 5}`,
      fill: 'none', stroke: 'var(--ink-muted, #6b6b6b)', 'stroke-width': 1, 'aria-hidden': 'true'
    }));
  }

  return root;
}

function roundedSegment(x, y, w, h, r, roundLeft, roundRight) {
  const rl = roundLeft ? Math.min(r, w / 2) : 0;
  const rr = roundRight ? Math.min(r, w / 2) : 0;
  return `M${x + rl},${y}
          L${x + w - rr},${y} ${rr ? `Q${x + w},${y} ${x + w},${y + rr}` : ''}
          L${x + w},${y + h - rr} ${rr ? `Q${x + w},${y + h} ${x + w - rr},${y + h}` : ''}
          L${x + rl},${y + h} ${rl ? `Q${x},${y + h} ${x},${y + h - rl}` : ''}
          L${x},${y + rl} ${rl ? `Q${x},${y} ${x + rl},${y}` : ''} Z`;
}

// ---------------------------------------------------------------------------
// Donut - mutually exclusive statuses, with a total in the center
// ---------------------------------------------------------------------------

export function donutChart(container, opts) {
  const { segments, formatValue = fmtInt, height = 220, valueLabel = 'licenses',
    totalLabel = 'of all licenses', centerLabel = 'licenses', showCenter = true,
    innerRadiusRatio = 0.64, emptyColor = null } = opts;
  const width = container.clientWidth || container.parentElement?.clientWidth || 240;
  const root = mount(container, width, height);
  const visible = segments.filter(segment => Number.isFinite(segment.value) && segment.value > 0);
  const total = visible.reduce((sum, segment) => sum + segment.value, 0);
  const cx = width / 2;
  const cy = height / 2;
  const outer = Math.max(1, Math.min(width, height) / 2 - 8);
  const inner = outer * innerRadiusRatio;
  const point = (radius, angle) => `${cx + radius * Math.cos(angle)},${cy + radius * Math.sin(angle)}`;
  let angle = -Math.PI / 2;

  if (!total && emptyColor) {
    root.appendChild(svg('circle', { class: 'donut-empty', cx, cy, r: (outer + inner) / 2,
      fill: 'none', stroke: emptyColor, 'stroke-width': outer - inner, 'aria-hidden': 'true' }));
  }

  visible.forEach(segment => {
    const sweep = segment.value / total * Math.PI * 2;
    // Gaps belong only to rendered slices; a single category fills the ring.
    const gap = visible.length > 1 ? Math.min(GAP / outer, sweep / 5) : 0;
    const start = angle + gap / 2;
    const end = angle + sweep - gap / 2;
    const large = end - start > Math.PI ? 1 : 0;
    const d = visible.length === 1
      ? `M${point(outer, start)} A${outer},${outer} 0 1 1 ${point(outer, start + Math.PI)}
          A${outer},${outer} 0 1 1 ${point(outer, start + 2 * Math.PI)}
          L${point(inner, start)} A${inner},${inner} 0 1 0 ${point(inner, start + Math.PI)}
          A${inner},${inner} 0 1 0 ${point(inner, start)} Z`
      : `M${point(outer, start)} A${outer},${outer} 0 ${large} 1 ${point(outer, end)}
          L${point(inner, end)} A${inner},${inner} 0 ${large} 0 ${point(inner, start)} Z`;
    const share = `${(segment.value / total * 100).toFixed(1)}%`;
    const mark = svg('path', { class: 'mark donut-segment', d, fill: segment.color,
      role: 'img', 'aria-label': `${segment.label}: ${formatValue(segment.value)} ${valueLabel}, ${share}` });
    attachTip(mark, () => ({ title: segment.label, rows: [
      { label: valueLabel, value: formatValue(segment.value), color: segment.color, shape: 'dot' },
      { label: totalLabel, value: share }
    ] }));
    root.appendChild(mark);
    angle += sweep;
  });

  if (!showCenter) return root;
  const totalText = formatValue(total);
  const count = svg('text', { x: cx, y: cy - 1, 'text-anchor': 'middle', fill: 'var(--ink, #1a1a1a)',
    'font-size': Math.min(30, Math.max(12, (inner * 2 - 12) / (totalText.length * 0.65))), 'font-weight': 600 });
  count.textContent = totalText;
  const label = svg('text', { x: cx, y: cy + 22, 'text-anchor': 'middle',
    fill: 'var(--ink-muted, #6b6b6b)', 'font-size': 12 });
  label.textContent = centerLabel;
  root.append(count, label);
  return root;
}

// ---------------------------------------------------------------------------
// Sparkline - the de-emphasised 12-point trend on a stat tile
// ---------------------------------------------------------------------------

export function sparkline(container, values, { color = 'var(--series-1)', height = 34 } = {}) {
  const clean = values.filter(Number.isFinite);
  if (clean.length < 2) {
    clear(container);
    return null;
  }
  const width = 180;
  const root = mount(container, width, height);
  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const span = max - min || 1;
  const pts = clean.map((v, i) => [
    (i / (clean.length - 1)) * (width - 6) + 3,
    height - 4 - ((v - min) / span) * (height - 10)
  ]);

  root.appendChild(
    svg('path', {
      d: `M${pts[0][0]},${height} L` + pts.map(p => `${p[0]},${p[1]}`).join(' L') + ` L${pts.at(-1)[0]},${height} Z`,
      fill: color,
      'fill-opacity': 0.1
    })
  );
  root.appendChild(
    svg('path', {
      d: `M${pts.map(p => `${p[0]},${p[1]}`).join(' L')}`,
      fill: 'none',
      stroke: color,
      'stroke-width': 2,
      'stroke-linejoin': 'round',
      'stroke-linecap': 'round'
    })
  );
  const last = pts.at(-1);
  root.appendChild(svg('circle', { cx: last[0], cy: last[1], r: 3.5, fill: color, stroke: SURFACE, 'stroke-width': 2 }));
  return root;
}

export { svg, clear };
